// @mui material components
import Grid from "@mui/material/Grid";

// Admin panel React components
import MDBox from "components/MDBox";

// Admin panel React example components
import * as React from 'react';

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import Footer from "examples/Footer";

// Firestore
import { db } from "../../firebase";
import { collection, query, where, onSnapshot, Timestamp } from "firebase/firestore";

// Data
import { useState, useEffect } from "react";

const localeMapping = {
  en_CA: "English",
  fr_CA: "French",
  es_LATAM: "Spanish",
  // Add more mappings as needed
};

function Dashboard() {
  const [usersToday, setUsersToday] = useState(0);
  const [usersPerHour, setUsersPerHour] = useState([]);
  const [usersPerDay, setUsersPerDay] = useState([]);
  const [usersPerMonth, setUsersPerMonth] = useState([]);
  const [localesToday, setLocalesToday] = useState({});
  const [localesWeek, setLocalesWeek] = useState({});
  const [localesMonth, setLocalesMonth] = useState({});

  useEffect(() => {
    const now = Timestamp.now();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - 7);
    startOfWeek.setHours(0, 0, 0, 0);

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const usersQuery = query(collection(db, "Users"), where("accountCreated", ">=", startOfMonth));

    const unsubscribe = onSnapshot(usersQuery, (querySnapshot) => {
      let todayCount = 0;
      const hourlyCounts = new Array(24).fill(0);
      const dailyCounts = new Array(7).fill(0);
      const monthlyCounts = new Array(31).fill(0);
      const localesTodayCount = {};
      const localesWeekCount = {};
      const localesMonthCount = {};

      querySnapshot.forEach((doc) => {
        const user = doc.data();
        const accountCreated = user.accountCreated ? user.accountCreated.toDate() : null;

        if (accountCreated) {
          if (accountCreated >= startOfToday) {
            todayCount++;
            hourlyCounts[accountCreated.getHours()]++;
            localesTodayCount[user.locale] = (localesTodayCount[user.locale] || 0) + 1;
          }

          if (accountCreated >= startOfWeek) {
            const dayOfWeek = accountCreated.getDay();
            dailyCounts[dayOfWeek]++;
            localesWeekCount[user.locale] = (localesWeekCount[user.locale] || 0) + 1;
          }

          if (accountCreated >= startOfMonth) {
            const dayOfMonth = accountCreated.getDate() - 1;
            monthlyCounts[dayOfMonth]++;
            localesMonthCount[user.locale] = (localesMonthCount[user.locale] || 0) + 1;
          }
        }
      });

      setUsersToday(todayCount);
      setUsersPerHour(hourlyCounts);
      setUsersPerDay(dailyCounts);
      setUsersPerMonth(monthlyCounts);
      setLocalesToday(localesTodayCount);
      setLocalesWeek(localesWeekCount);
      setLocalesMonth(localesMonthCount);
    });

    return () => unsubscribe();
  }, []);

  const getLocalePercentage = (localeCount, totalCount) => {
    return totalCount > 0 ? ((localeCount / totalCount) * 100).toFixed(2) : 0;
  };

  const renderLocalePercentage = (locales, totalCount) => {
    return Object.keys(locales).map((locale) => (
      <div key={locale}>
        {localeMapping[locale] || locale}: {getLocalePercentage(locales[locale], totalCount)}%
      </div>
    ));
  };

  const hourlyChartData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: { label: "Users per Hour", data: usersPerHour },
  };

  const dailyChartData = {
    labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    datasets: { label: "Users per Day", data: usersPerDay },
  };

  const monthlyChartData = {
    labels: Array.from({ length: 31 }, (_, i) => `${i + 1}`),
    datasets: { label: "Users per Month", data: usersPerMonth },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="person_add"
                title="Users Added Today"
                count={usersToday}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Just updated",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="language"
                title="Languages Today"
                count=""
                percentage={{
                  color: "success",
                  amount: renderLocalePercentage(localesToday, usersToday),
                  label: "Just updated",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title="Users per Hour"
                  description="Users added per hour today"
                  date="updated just now"
                  chart={hourlyChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="success"
                  title="Users per Day"
                  description="Users added per day in the last week"
                  date="updated just now"
                  chart={dailyChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="dark"
                  title="Users per Month"
                  description="Users added per day in the last month"
                  date="updated just now"
                  chart={monthlyChartData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ComplexStatisticsCard
                  color="info"
                  icon="language"
                  title="Languages This Week"
                  count=""
                  percentage={{
                    color: "success",
                    amount: renderLocalePercentage(localesWeek, usersPerDay.reduce((a, b) => a + b, 0)),
                    label: "Just updated",
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ComplexStatisticsCard
                  color="success"
                  icon="language"
                  title="Languages This Month"
                  count=""
                  percentage={{
                    color: "success",
                    amount: renderLocalePercentage(localesMonth, usersPerMonth.reduce((a, b) => a + b, 0)),
                    label: "Just updated",
                  }}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;